import businessTypesData from '@/assets/business-types.json';
import { useState } from 'react';
import { Button } from '@components/Button';
import { ModalLayout } from '@components/ModalLayout';
import { AddNewTypeModal, type MockMetadata } from '@components/AddNewTypeModal';
import { EditMetadataModal } from '@components/EditMetadataModal';
import { DeleteLeadModal } from '@components/DeleteLeadModal';
import { SectionHeader } from './shared';
import styles from '../DemoPage.module.scss';

// Mock AI-generated data for demo
const MOCK_METADATA: MockMetadata = {
  website_objectives: [
    'Online appointment booking and scheduling',
    'Service information and pricing transparency',
    'Customer testimonials and reviews showcase',
    'Contact information and location details',
    'Staff profiles and qualifications',
  ],
  industry_painpoints: [
    'Difficulty attracting new customers online',
    'Managing online reputation and reviews',
    'Competing with larger chain businesses',
    'Keeping website content fresh and updated',
    'Converting website visitors into paying customers',
  ],
};

export const ModalsSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTypeModalOpen, setIsAddTypeModalOpen] = useState(false);
  const [isEditMetadataModalOpen, setIsEditMetadataModalOpen] = useState(false);
  const [isDeleteLeadModalOpen, setIsDeleteLeadModalOpen] = useState(false);
  const [existingTypes, setExistingTypes] = useState<string[]>(['Cafe', 'Dental Clinic']);

  // Simulates the save handler from LeadsPage with proper callback handling
  const handleSaveType = (typeName: string, onExist: () => void, onCreated: () => void) => {
    // Check if type already exists in our demo list
    const exists = existingTypes.some(
      (existing) => existing.toLowerCase() === typeName.toLowerCase()
    );

    if (exists) {
      onExist();
      return;
    }

    // Simulate creating the type and triggering AI generation
    // In the real app, this would call Firebase and listen for updates
    // Here we simulate a delay then call onCreated which triggers the results state
    setTimeout(() => {
      // Add to existing types so it shows as "exists" if looked up again
      setExistingTypes((prev) => [...prev, typeName]);
      onCreated();
    }, 1500);
  };

  const handleDiscardType = (typeName: string) => {
    // Remove from existing types if it was just added
    setExistingTypes((prev) => prev.filter((t) => t.toLowerCase() !== typeName.toLowerCase()));
  };

  const handleOpenExisting = (typeName: string) => {
    // In the real app, this would navigate to the type
    alert(`Would navigate to existing type: ${typeName}`);
  };

  return (
    <>
      <section className={styles.section}>
        <SectionHeader
          title="Modals"
          description="Dialog components for focused user interactions."
          figmaNodeId="137-1048"
          styles={styles}
        />

        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Modal Layout</h3>
          <div className={styles.row}>
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              Open Modal Layout
            </Button>
          </div>
        </div>

        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Add New Type Modal</h3>
          <p className={styles.description}>
            Full modal flow with TypeAhead search, loading animation, and AI-generated results.
            Try "{existingTypes[0]}" for the "already exists" flow, or any other type to see results.
          </p>
          <div className={styles.row}>
            <Button variant="primary" onClick={() => setIsAddTypeModalOpen(true)}>
              Open Add New Type Modal
            </Button>
          </div>
        </div>

        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Edit Metadata Modal</h3>
          <p className={styles.description}>
            Modal for editing existing business type metadata including Website Objectives,
            Industry Painpoints, and Default Apollo Sequence.
          </p>
          <div className={styles.row}>
            <Button variant="primary" onClick={() => setIsEditMetadataModalOpen(true)}>
              Open Edit Metadata Modal
            </Button>
          </div>
        </div>

        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Delete Lead Modal</h3>
          <p className={styles.description}>
            Destructive confirmation dialog with filled Cancel (safe action) and outlined Delete
            (danger action). Error messages appear inline below the confirmation text.
          </p>
          <div className={styles.row}>
            <Button variant="primary" onClick={() => setIsDeleteLeadModalOpen(true)}>
              Open Delete Lead Modal
            </Button>
          </div>
        </div>
      </section>

      {/* Modal Layout Demo */}
      {isModalOpen && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setIsModalOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsModalOpen(false)}
          role="presentation"
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers to prevent backdrop click propagation */}
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ModalLayout
              title="Example Modal"
              onDismiss={() => setIsModalOpen(false)}
              onPrimaryAction={() => {
                alert('Saved!');
                setIsModalOpen(false);
              }}
              onSecondaryAction={() => setIsModalOpen(false)}
            >
              <div style={{ padding: '24px' }}>
                <p>This is example modal content. You can put any content here.</p>
              </div>
            </ModalLayout>
          </div>
        </div>
      )}

      {/* Add New Type Modal */}
      <AddNewTypeModal
        isOpen={isAddTypeModalOpen}
        onClose={() => setIsAddTypeModalOpen(false)}
        onSave={handleSaveType}
        onDiscard={handleDiscardType}
        onOpenExisting={handleOpenExisting}
        availableTypes={businessTypesData}
        existingTypes={existingTypes}
        mockData={MOCK_METADATA}
        mockDelay={2000}
      />

      {/* Edit Metadata Modal */}
      <EditMetadataModal
        isOpen={isEditMetadataModalOpen}
        onClose={() => setIsEditMetadataModalOpen(false)}
        typeName="Dental Clinics"
        websiteObjectives={[
          'Attract and acquire new local patients',
          'Enable 24/7 online appointment scheduling',
          'Showcase services to build credibility and trust',
        ]}
        industryPainpoints={[
          'High volume of administrative phone calls',
          'Frequent patient no-shows and last-minute cancellations',
          'Lack of patient education and treatment understanding',
        ]}
        onSave={(data) => {
          alert(`Saved metadata:\n${JSON.stringify(data, null, 2)}`);
        }}
      />

      {/* Delete Lead Modal */}
      <DeleteLeadModal
        isOpen={isDeleteLeadModalOpen}
        onClose={() => setIsDeleteLeadModalOpen(false)}
        leadName="Aurora Clothing Co."
        onConfirm={async () => {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          alert('Lead deleted (demo)');
        }}
      />
    </>
  );
};
